import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Ban } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn, formatVND, formatCompact } from '@/lib/utils';
import * as ledger from '../../../ledger/ledger';
import { addPlanned } from '../../../ledger/plans';
import { useData } from '../../store/data';
import { useEntrySheet } from './useEntrySheet';
import TransferPanel from './TransferPanel';
import { entryFormSchema, zodResolver, parseAmount, toPayload, previewPolicy, isFutureDate, type EntryFormValues, type EntryFormKind } from './formSchema';

const KINDS: Array<{ id: EntryFormKind; label: string }> = [
  { id: 'expense', label: 'Chi' }, { id: 'income', label: 'Thu' }, { id: 'refund', label: 'Hoàn tiền' }
];

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const LAST = (k: EntryFormKind) => `v2-last-wallet-${k === 'income' ? 'income' : 'expense'}`;
const remember = (k: EntryFormKind, id: string) => { try { localStorage.setItem(LAST(k), id); } catch { /* bỏ qua */ } };
const recall = (k: EntryFormKind) => { try { return localStorage.getItem(LAST(k)) ?? ''; } catch { return ''; } };

export default function EntrySheet() {
  const { open, editing, kind: newKind, close } = useEntrySheet();
  const sources = useData(s => s.data.sources);
  const expenseCats = useData(s => s.data.expenseCategories);
  const incomeCats = useData(s => s.data.incomeCategories);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [transfer, setTransfer] = useState(false); // tab "Chuyển" (chuyển giữa hai ví) thay cho form thu/chi
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [future, setFuture] = useState<EntryFormValues | null>(null);
  const plannedExpenses = useData(s => s.data.plannedExpenses);
  const plannedIncomes = useData(s => s.data.plannedIncomes);
  const isDesktop = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches;

  const form = useForm<EntryFormValues>({ resolver: zodResolver(entryFormSchema) as any, defaultValues: { kind: 'expense', amountText: '', category: '', walletId: '', date: isoDate(new Date()), description: '' } });
  const { register, watch, setValue, handleSubmit, reset, formState: { errors } } = form;
  const kind = watch('kind');
  const walletId = watch('walletId');
  const category = watch('category');
  const amountText = watch('amountText');

  const wallets = useMemo(
    () => sources.filter(s => s.status !== 'inactive' && (kind === 'income' ? s.type !== 'credit' : true)),
    [sources, kind]
  );
  const categories = useMemo(() => {
    const list = (kind === 'income' ? incomeCats : expenseCats)
      .slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
      .map(c => ({ name: c.name as string, icon: c.icon as string | undefined, color: c.color as string | undefined }));
    const seen = new Set<string>();
    const uniq = list.filter(c => (seen.has(c.name) ? false : (seen.add(c.name), true)));
    // Bản ghi cũ có thể mang danh mục không còn trong Cài đặt — vẫn phải chọn được để sửa
    if (category && !seen.has(category)) uniq.push({ name: category, icon: undefined, color: undefined });
    return uniq;
  }, [kind, incomeCats, expenseCats, category]);

  // Nạp giá trị mỗi lần mở
  useEffect(() => {
    if (!open) return;
    setServerError(null); setConfirmDelete(false); setTransfer(false);
    if (editing) {
      const r = editing.raw;
      const d = editing.date;
      reset({
        kind: editing.kind === 'income' ? 'income' : editing.kind === 'refund' ? 'refund' : 'expense',
        amountText: String(Math.abs(Number(r.amount) || 0)),
        category: r.categoryName || r.category || '',
        walletId: r.walletId ?? r.sourceId ?? '',
        date: isoDate(d),
        description: r.description || r.note || r.notes || '',
        isLoanProceeds: !!r.isLoanProceeds
      });
    } else {
      const last = recall(newKind);
      const first = sources.filter(s => newKind === 'income' ? s.type !== 'credit' : true).find(s => s.id === last)?.id
        ?? sources.find(s => s.status !== 'inactive' && (newKind === 'income' ? s.type !== 'credit' : s.type !== 'credit'))?.id ?? '';
      reset({ kind: newKind, amountText: '', category: '', walletId: first, date: isoDate(new Date()), description: '', isLoanProceeds: false });
    }
  }, [open, editing, newKind]); // eslint-disable-line react-hooks/exhaustive-deps

  // Đổi loại: ví tín dụng không nhận thu nhập
  useEffect(() => {
    if (kind === 'income' && sources.find(s => s.id === walletId)?.type === 'credit') setValue('walletId', '');
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const wallet = sources.find(s => s.id === walletId);
  const amount = parseAmount(amountText);
  const policy = editing ? { level: 'ok' as const } : previewPolicy(kind, amount, wallet);

  const uid = getAuth().currentUser?.uid ?? '';

  /** Ngày ở tương lai: không ghi vào sổ ngay (sẽ làm sai số dư hôm nay) mà gợi ý đặt thành khoản dự kiến. */
  const onSubmit = handleSubmit(async (values) => {
    if (!editing && values.kind !== 'refund' && isFutureDate(values.date)) { setFuture(values); return; }
    await save(values);
  });

  const planInstead = async (values: EntryFormValues) => {
    setSaving(true);
    const w = wallets.find(x => x.id === values.walletId);
    const r = await addPlanned(uid, values.kind === 'income' ? 'income' : 'expense', {
      amount: parseAmount(values.amountText), category: values.category, walletId: values.walletId, walletName: w?.name, date: values.date, note: values.description
    });
    setSaving(false); setFuture(null);
    if (!r.success) { setServerError(r.error ?? 'Không tạo được khoản dự kiến'); return; }
    toast.success('Đã đặt thành khoản dự kiến — xác nhận ở mục Dự kiến khi đến ngày');
    close();
  };

  const save = async (values: EntryFormValues) => {
    setServerError(null); setSaving(true);
    const { collection, data } = toPayload(values, categories, wallets.map(w => ({ id: w.id, name: w.name })));
    const api = collection === 'incomes'
      ? { add: ledger.addIncome, update: ledger.updateIncome, del: ledger.deleteIncome }
      : { add: ledger.addExpense, update: ledger.updateExpense, del: ledger.deleteExpense };
    const res = editing ? await api.update(uid, editing.id, data) : await api.add(uid, data);
    setSaving(false);
    if (!res.success) { setServerError(res.error ?? 'Không lưu được.'); return; }
    remember(values.kind, values.walletId);
    res.warnings?.forEach(w => toast.warning(w, { duration: 7000 }));
    if (editing) toast.success('Đã cập nhật');
    else {
      toast.success(`Đã ghi ${formatVND(data.amount)} — ${values.category}`, {
        duration: 10000,
        action: { label: 'Hoàn tác', onClick: async () => {
          const u = await api.del(uid, res.id!);
          u.success ? toast.success('Đã hoàn tác') : toast.error(u.error ?? 'Không hoàn tác được');
        } }
      });
    }
    close();
  };

  const onDelete = async () => {
    if (!editing) return;
    setSaving(true);
    const del = editing.kind === 'income' ? ledger.deleteIncome : ledger.deleteExpense;
    // Giao dịch cũ chưa có liên kết ngược: tìm khoản dự kiến đã sinh ra nó để mở lại
    const isIncome = editing.kind === 'income';
    const link = (isIncome ? plannedIncomes : plannedExpenses).find((p: any) => p.status === 'confirmed' && p[isIncome ? 'actualIncomeId' : 'actualExpenseId'] === editing.id);
    const res = await del(uid, editing.id, link ? { reopen: { collection: isIncome ? 'plannedIncomes' : 'plannedExpenses', id: link.id } } : undefined);
    setSaving(false); setConfirmDelete(false);
    if (!res.success) { setServerError(res.error ?? 'Không xoá được.'); return; }
    res.warnings?.forEach(w => toast.warning(w, { duration: 7000 }));
    toast.success(res.reopened ? 'Đã xoá — số dư ví được hoàn lại và khoản dự kiến đã mở lại ở mục Dự kiến' : 'Đã xoá — số dư ví đã được hoàn lại');
    close();
  };

  const err = (k: keyof EntryFormValues) => errors[k]?.message as string | undefined;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && close()}>
        <SheetContent side={isDesktop ? 'right' : 'bottom'} className="max-h-[92vh] overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{editing ? 'Sửa giao dịch' : 'Ghi nhanh'}</SheetTitle>
            <SheetDescription>{editing ? 'Số dư ví được điều chỉnh theo thay đổi của bạn.' : transfer ? 'Số tiền → ví nguồn → ví đích → Chuyển.' : 'Số tiền → danh mục → ví → Lưu.'}</SheetDescription>
          </SheetHeader>

          {!editing && (
            <Tabs className="mt-4" value={transfer ? 'transfer' : kind} onValueChange={(v) => { if (v === 'transfer') setTransfer(true); else { setTransfer(false); setValue('kind', v as EntryFormKind); } }}>
              <TabsList className="grid w-full grid-cols-4">
                {KINDS.map(k => <TabsTrigger key={k.id} value={k.id} className="px-1">{k.label}</TabsTrigger>)}
                <TabsTrigger value="transfer" className="px-1">Chuyển</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {transfer && !editing ? <div className="mt-4"><TransferPanel onDone={close} /></div> : (
          <form onSubmit={onSubmit} className="mt-4 space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="amount">Số tiền</Label>
              <Input id="amount" inputMode="text" autoComplete="off" autoFocus placeholder="vd: 50k, 1.5tr" className="num text-lg" aria-invalid={!!errors.amountText} {...register('amountText')} />
              <p className={cn('num text-xs', err('amountText') ? 'text-bad' : 'text-muted-foreground')}>
                {err('amountText') ?? (amount > 0 ? `= ${formatVND(amount)}` : 'Gõ tắt: 50k · 1.5tr · 2 tỷ')}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Danh mục</Label>
              <Select value={category} onValueChange={(v) => setValue('category', v, { shouldValidate: true })}>
                <SelectTrigger aria-invalid={!!errors.category}><SelectValue placeholder="Chọn danh mục" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {err('category') && <p className="text-xs text-bad">{err('category')}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>{kind === 'income' ? 'Ví nhận tiền' : kind === 'refund' ? 'Ví nhận hoàn' : 'Ví / thẻ thanh toán'}</Label>
              <Select value={walletId} onValueChange={(v) => setValue('walletId', v, { shouldValidate: true })}>
                <SelectTrigger aria-invalid={!!errors.walletId}><SelectValue placeholder="Chọn ví" /></SelectTrigger>
                <SelectContent>
                  {wallets.map(w => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} · {w.type === 'credit'
                        ? `thẻ, còn ${formatCompact(Math.max(0, (Number(w.creditLimit) || 0) - (Number(w.usedCredit) || 0)))}`
                        : formatCompact(Number(w.balance) || 0)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {err('walletId') && <p className="text-xs text-bad">{err('walletId')}</p>}
              {policy.level !== 'ok' && (
                <p role={policy.level === 'block' ? 'alert' : 'status'} className={cn('flex items-start gap-1.5 rounded-md px-2.5 py-2 text-xs', policy.level === 'block' ? 'bg-bad-soft text-bad' : 'bg-warn-soft text-warn')}>
                  {policy.level === 'block' ? <Ban size={14} className="mt-0.5 shrink-0" aria-hidden /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />}
                  {policy.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="date">Ngày</Label>
                <Input id="date" type="date" className="num" aria-invalid={!!errors.date} {...register('date')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="desc">Ghi chú</Label>
                <Input id="desc" autoComplete="off" placeholder="Tuỳ chọn" {...register('description')} />
              </div>
            </div>

            {kind === 'income' && (
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" {...register('isLoanProceeds')} />
                <span>
                  <span className="font-medium">Đây là tiền vay</span>
                  <span className="block text-xs text-muted-foreground">Tiền vào ví nhưng không tính là thu nhập (không làm đẹp tỷ lệ tiết kiệm). Nên thêm khoản vay ở Tài sản & Nợ để ghi nợ tương ứng.</span>
                </span>
              </label>
            )}

            {serverError && <p role="alert" className="rounded-md bg-bad-soft px-3 py-2 text-sm text-bad">{serverError}</p>}

            <div className="flex items-center gap-2 pt-1">
              {editing && (
                <Button type="button" variant="outline" size="icon" onClick={() => setConfirmDelete(true)} aria-label="Xoá giao dịch" disabled={saving}>
                  <Trash2 size={16} />
                </Button>
              )}
              <Button type="submit" className="flex-1" disabled={saving || policy.level === 'block'}>
                {saving ? 'Đang lưu…' : editing ? 'Lưu thay đổi' : 'Lưu'}
              </Button>
            </div>
          </form>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!future} onOpenChange={(o) => !o && setFuture(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ngày này ở tương lai</DialogTitle>
            <DialogDescription>Ghi vào sổ ngay sẽ trừ/cộng ví từ hôm nay. Nếu đây là khoản sắp tới, hãy đặt thành khoản dự kiến rồi xác nhận khi đến ngày.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => future && save(future).then(() => setFuture(null))} disabled={saving}>Vẫn ghi vào sổ</Button>
            <Button onClick={() => future && planInstead(future)} disabled={saving}>Đặt làm khoản dự kiến</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Xoá giao dịch này?</DialogTitle>
            <DialogDescription>Số dư ví sẽ được hoàn lại tương ứng. Có thể không xoá được nếu tiền đã được dùng hết.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Giữ lại</Button>
            <Button variant="destructive" onClick={onDelete} disabled={saving}>Xoá</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
